import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Heading, Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import Footer from '../../components/Footer/Footer';

import css from './PrivacyPolicyPage.module.css';

const LEGAL_INFO = {
  marketplaceName: 'DoneThat',
  websiteUrl: 'https://getdonethat.com',
  companyAddress: '3400 Cottage Way, Ste G2 #13236, Sacramento, CA 95825, United States',
  country: 'United States',
  contactEmail: 'hello@getdonethat.com',
  lastUpdated: 'May 04, 2026',
};

const TOC = [
  { id: 'definitions', number: '1', title: 'Interpretation and definitions' },
  { id: 'contact', number: '2', title: 'Contact us' },
  { id: 'collecting', number: '3', title: 'Collecting and using your personal data' },
  { id: 'processors', number: '4', title: 'Detailed information on processing' },
  { id: 'links', number: '5', title: 'Links to other websites' },
  { id: 'gdpr', number: '6', title: 'GDPR privacy' },
  { id: 'ccpa', number: '7', title: 'CCPA & California rights' },
  { id: 'children', number: '8', title: "Children's privacy rights" },
  { id: 'changes', number: '9', title: 'Changes to this Privacy Policy' },
];

const Term = ({ name, children }) => (
  <>
    <dt className={css.dt}>{name}</dt>
    <dd className={css.dd}>{children}</dd>
  </>
);

/* ============================================================================
   PrivacyArticleBody — full article markup. Used by both the standalone page
   and the modal-context content shim (consumed by AuthenticationPage).
   ============================================================================ */
const PrivacyArticleBody = () => (
  <article className={css.article}>
    <p className={css.lead}>
      This Privacy Policy describes Our policies and procedures on the collection, use, and
      disclosure of Your information when You use {LEGAL_INFO.marketplaceName}, and tells You
      about Your privacy rights and how the law protects You.
    </p>
    <p className={css.p}>
      We use Your personal data to provide and improve the Service. By using the Service, You
      agree to the collection and use of information in accordance with this Privacy Policy.
    </p>

    {/* 1 — Definitions */}
    <section id="definitions" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>1</span> Interpretation and definitions
      </h2>

      <h3 className={css.h3}>1.1 — Interpretation</h3>
      <p className={css.p}>
        The words of which the initial letter is capitalized have meanings defined under the
        following conditions. The following definitions shall have the same meaning regardless of
        whether they appear in the singular or in the plural.
      </p>

      <h3 className={css.h3}>1.2 — Definitions</h3>
      <p className={css.p}>For the purposes of this Privacy Policy:</p>
      <dl className={css.dl}>
        <Term name="Account">
          means a unique account created for You to access our Service or parts of our Service.
        </Term>
        <Term name="Affiliate">
          means an entity that controls, is controlled by, or is under common control with a party.
        </Term>
        <Term name="Application">
          means the software program provided by the Company, named {LEGAL_INFO.marketplaceName}.
        </Term>
        <Term name="Business">
          refers to the Company as the legal entity that collects Consumers&apos; personal
          information and determines the purposes and means of the processing of that information.
        </Term>
        <Term name="Company">
          (referred to as either &ldquo;the Company&rdquo;, &ldquo;We&rdquo;, &ldquo;Us&rdquo; or
          &ldquo;Our&rdquo; in this Agreement) refers to {LEGAL_INFO.marketplaceName},{' '}
          {LEGAL_INFO.companyAddress}. For the purpose of the GDPR, the
          Company is the Data Controller.
        </Term>
        <Term name="Consumer">refers to You.</Term>
        <Term name="Cookies">
          are small files that are placed on Your computer, mobile device or any other device by a
          website, containing the details of Your browsing history on that website among its many
          uses.
        </Term>
        <Term name="Country">refers to {LEGAL_INFO.country}.</Term>
        <Term name="Data Controller">
          for the purposes of the GDPR, refers to the Company as the legal person which alone or
          jointly with others determines the purposes and means of the processing of Personal Data.
        </Term>
        <Term name="Device">
          means any device that can access the Service such as a computer, a cellphone, or a
          digital tablet.
        </Term>
        <Term name="Do Not Track (DNT)">
          is a concept that has been promoted by US regulatory authorities for the Internet
          industry to develop and implement a mechanism for allowing internet users to control the
          tracking of their online activities across websites.
        </Term>
        <Term name="Personal Data">
          is any information that relates to an identified or identifiable individual.
        </Term>
        <Term name="Service">refers to the Application or the Website or both.</Term>
        <Term name="Service Provider">
          means any natural or legal person who processes the data on behalf of the Company. It
          refers to third-party companies or individuals employed by the Company to facilitate the
          Service, to provide the Service on behalf of the Company, to perform services related to
          the Service, or to assist the Company in analyzing how the Service is used.
        </Term>
        <Term name="Third-party Social Media Service">
          refers to any website or any social network website through which a User can log in or
          create an account to use the Service.
        </Term>
        <Term name="Usage Data">
          refers to data collected automatically, either generated by the use of the Service or
          from the Service infrastructure itself (for example, the duration of a page visit).
        </Term>
        <Term name="Website">
          refers to {LEGAL_INFO.marketplaceName}, accessible from {LEGAL_INFO.websiteUrl}.
        </Term>
        <Term name="You">
          means the individual accessing or using the Service, or the company, or other legal
          entity on behalf of which such individual is accessing or using the Service. Under GDPR,
          You can be referred to as the Data Subject or User.
        </Term>
      </dl>
    </section>

    {/* 2 — Contact */}
    <section id="contact" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>2</span> Contact us
      </h2>
      <p className={css.p}>
        If you have any questions about this Privacy Policy, You can contact us:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>
          By email:{' '}
          <a href={`mailto:${LEGAL_INFO.contactEmail}`} className={css.link}>
            {LEGAL_INFO.contactEmail}
          </a>
        </li>
        <li className={css.li}>
          Through our help center:{' '}
          <NamedLink name="FAQPage" className={css.link}>
            FAQ &amp; Support
          </NamedLink>
        </li>
      </ul>
    </section>

    {/* 3 — Collecting */}
    <section id="collecting" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>3</span> Collecting and using your personal data
      </h2>

      <h3 className={css.h3}>3.1 — Types of data collected</h3>

      <h4 className={css.h4}>3.1.1 — Personal data</h4>
      <p className={css.p}>
        While using Our Service, We may ask You to provide Us with certain personally identifiable
        information that can be used to contact or identify You. Personally identifiable
        information may include, but is not limited to:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>Email address</li>
        <li className={css.li}>First name and last name</li>
        <li className={css.li}>Phone number</li>
        <li className={css.li}>Address, city, state, country, ZIP/postal code</li>
        <li className={css.li}>
          Bank account information (Experts only — for receiving payouts via Stripe Connect)
        </li>
        <li className={css.li}>
          Payment card details (handled directly by Stripe — not stored on Our servers)
        </li>
        <li className={css.li}>Profile photo and bio</li>
        <li className={css.li}>Usage Data</li>
      </ul>

      <h4 className={css.h4}>3.1.2 — Usage data</h4>
      <p className={css.p}>
        Usage Data is collected automatically when using the Service. It may include information
        such as Your Device&apos;s Internet Protocol address (e.g., IP address), browser type,
        browser version, the pages of our Service that You visit, the time and date of Your visit,
        the time spent on those pages, unique device identifiers, and other diagnostic data.
      </p>
      <p className={css.p}>
        When You access the Service via a mobile device, We may collect certain information
        automatically — including the type of mobile device You use, Your mobile device unique ID,
        the IP address of Your mobile device, Your mobile operating system, the type of mobile
        Internet browser You use, and other diagnostic data.
      </p>

      <h4 className={css.h4}>3.1.3 — Information from third-party social media services</h4>
      <p className={css.p}>
        The Company may allow You to create an account and log in to use the Service through
        Third-party Social Media Services. These may include, but are not limited to:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>Google</li>
        <li className={css.li}>Apple</li>
        <li className={css.li}>Facebook</li>
      </ul>
      <p className={css.p}>
        If You decide to register through or otherwise grant us access to a Third-Party Social
        Media Service, We may collect personal data that is already associated with Your
        Third-Party Social Media Service&apos;s account, such as Your name, Your email address,
        and Your profile picture.
      </p>

      <h4 className={css.h4}>3.1.4 — Tracking technologies and cookies</h4>
      <p className={css.p}>
        We use Cookies and similar tracking technologies to track the activity on Our Service and
        store certain information. Tracking technologies used are beacons, tags, and scripts to
        collect and track information and to improve and analyze Our Service.
      </p>
      <p className={css.p}>
        Cookies can be &ldquo;Persistent&rdquo; or &ldquo;Session&rdquo; Cookies. Persistent
        Cookies remain on Your device when You go offline, while Session Cookies are deleted as
        soon as You close Your web browser. We use both types for the purposes set out below:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>
          <strong>Necessary / Essential Cookies</strong> (Session, administered by Us) — essential
          to provide You with services through the Service, authenticate users, and prevent
          fraudulent use of accounts. Without these Cookies, the services cannot be provided.
        </li>
        <li className={css.li}>
          <strong>Cookies Notice Acceptance Cookies</strong> (Persistent, administered by Us) —
          identify if users have accepted the use of cookies on the Service.
        </li>
        <li className={css.li}>
          <strong>Functionality Cookies</strong> (Persistent, administered by Us) — allow Us to
          remember choices You make, such as login details or language preference.
        </li>
        <li className={css.li}>
          <strong>Tracking and Performance Cookies</strong> (Persistent, administered by third
          parties) — used to track information about traffic to the Service and how users interact
          with it.
        </li>
      </ul>

      <h3 className={css.h3}>3.2 — Use of your personal data</h3>
      <p className={css.p}>The Company may use Personal Data for the following purposes:</p>
      <ul className={css.ul}>
        <li className={css.li}>
          <strong>To provide and maintain our Service</strong>, including monitoring its usage.
        </li>
        <li className={css.li}>
          <strong>To manage Your Account</strong>, including Your registration as a user of the
          Service.
        </li>
        <li className={css.li}>
          <strong>For the performance of a contract</strong>: developing, complying with, and
          undertaking the Booking contract for Sessions or any other contract with Us through the
          Service.
        </li>
        <li className={css.li}>
          <strong>To contact You</strong>: by email, phone, SMS, or other equivalent forms of
          electronic communication regarding updates, security notices, or service-related
          communications.
        </li>
        <li className={css.li}>
          <strong>To send You newsletters and special offers</strong>, where You have consented and
          have not opted out.
        </li>
        <li className={css.li}>
          <strong>To manage Your requests</strong>: to attend to and manage any requests You make
          to Us.
        </li>
        <li className={css.li}>
          <strong>For business transfers</strong>: in connection with any merger, acquisition, or
          asset sale, where Personal Data may be among the assets transferred.
        </li>
        <li className={css.li}>
          <strong>For other purposes</strong>: data analysis, identifying usage trends, determining
          the effectiveness of Our marketing, and improving the Service and Your experience.
        </li>
      </ul>
      <p className={css.p}>We may share Your personal information in the following situations:</p>
      <ul className={css.ul}>
        <li className={css.li}>
          <strong>With Service Providers</strong>: to monitor and analyze use of Our Service,
          process payments, send transactional email, and contact You.
        </li>
        <li className={css.li}>
          <strong>For business transfers</strong>: in connection with any merger, financing, or
          acquisition.
        </li>
        <li className={css.li}>
          <strong>With Affiliates</strong>: where required, We will require those affiliates to
          honor this Privacy Policy.
        </li>
        <li className={css.li}>
          <strong>With other users</strong>: when You share information or otherwise interact in
          public areas (e.g., your public profile, listings, reviews), such information may be
          viewed by other users.
        </li>
        <li className={css.li}>
          <strong>With Your consent</strong>: for any other purpose with Your consent.
        </li>
      </ul>

      <h3 className={css.h3}>3.3 — Retention of your personal data</h3>
      <p className={css.p}>
        The Company will retain Your Personal Data only for as long as is necessary for the
        purposes set out in this Privacy Policy. We will retain and use Your Personal Data to the
        extent necessary to comply with our legal obligations, resolve disputes, and enforce our
        legal agreements and policies.
      </p>
      <p className={css.p}>
        Usage Data is generally retained for a shorter period of time, except when this data is
        used to strengthen the security or to improve the functionality of Our Service, or where
        We are legally obligated to retain it for longer.
      </p>

      <h3 className={css.h3}>3.4 — Transfer of your personal data</h3>
      <p className={css.p}>
        Your information, including Personal Data, is processed at the Company&apos;s operating
        offices and in any other places where the parties involved in the processing are located.
        This information may be transferred to — and maintained on — computers located outside of
        Your state, province, country, or other governmental jurisdiction where data protection
        laws may differ from those in Your jurisdiction.
      </p>
      <p className={css.p}>
        Your consent to this Privacy Policy followed by Your submission of such information
        represents Your agreement to that transfer. The Company will take all steps reasonably
        necessary to ensure that Your data is treated securely and in accordance with this Privacy
        Policy.
      </p>

      <h3 className={css.h3}>3.5 — Delete your personal data</h3>
      <p className={css.p}>
        You have the right to delete or request that We assist in deleting the Personal Data that
        We have collected about You. Our Service may give You the ability to delete certain
        information about You from within the Service.
      </p>
      <p className={css.p}>
        You may update, amend, or delete Your information at any time by signing in to Your
        Account and visiting the account settings section. You may also contact Us to request
        access to, correct, or delete any personal information that You have provided to Us.
      </p>
      <p className={css.p}>
        Please note that We may need to retain certain information when we have a legal obligation
        or lawful basis to do so.
      </p>

      <h3 className={css.h3}>3.6 — Disclosure of your personal data</h3>

      <h4 className={css.h4}>3.6.1 — Business transactions</h4>
      <p className={css.p}>
        If the Company is involved in a merger, acquisition, or asset sale, Your Personal Data may
        be transferred. We will provide notice before Your Personal Data is transferred and
        becomes subject to a different Privacy Policy.
      </p>

      <h4 className={css.h4}>3.6.2 — Law enforcement</h4>
      <p className={css.p}>
        Under certain circumstances, the Company may be required to disclose Your Personal Data if
        required to do so by law or in response to valid requests by public authorities (e.g., a
        court or a government agency).
      </p>

      <h4 className={css.h4}>3.6.3 — Other legal requirements</h4>
      <p className={css.p}>
        The Company may disclose Your Personal Data in the good-faith belief that such action is
        necessary to:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>Comply with a legal obligation</li>
        <li className={css.li}>Protect and defend the rights or property of the Company</li>
        <li className={css.li}>Prevent or investigate possible wrongdoing in connection with the Service</li>
        <li className={css.li}>Protect the personal safety of users of the Service or the public</li>
        <li className={css.li}>Protect against legal liability</li>
      </ul>

      <h3 className={css.h3}>3.7 — Security of your personal data</h3>
      <p className={css.p}>
        The security of Your Personal Data is important to Us. While We strive to use commercially
        acceptable means to protect Your Personal Data — including SSL encryption in transit and
        access controls at rest — no method of transmission over the Internet or electronic
        storage is 100% secure, and We cannot guarantee its absolute security.
      </p>
    </section>

    {/* 4 — Detailed processing */}
    <section id="processors" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>4</span> Detailed information on processing
      </h2>
      <p className={css.p}>
        The Service Providers We use may have access to Your Personal Data. These third-party
        vendors collect, store, use, process, and transfer information about Your activity on Our
        Service in accordance with their privacy policies.
      </p>

      <h3 className={css.h3}>4.1 — Analytics</h3>
      <p className={css.p}>
        We may use third-party Service Providers to monitor and analyze the use of our Service,
        including:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>
          <strong>Google Analytics</strong> — a web analytics service offered by Google that tracks
          and reports website traffic. Privacy policy:{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={css.link}
          >
            policies.google.com/privacy
          </a>
          .
        </li>
      </ul>

      <h3 className={css.h3}>4.2 — Email communication</h3>
      <p className={css.p}>
        We may use Your Personal Data to contact You with transactional notifications (booking
        confirmations, calendar invites, recordings) and — where You have consented — newsletters
        or product updates. You may opt out of marketing communications by following the
        unsubscribe link or instructions in any email We send, or by contacting Us. Transactional
        emails are required for Service delivery and cannot be opted out of without closing Your
        Account.
      </p>

      <h3 className={css.h3}>4.3 — Payments</h3>
      <p className={css.p}>
        We provide paid Sessions within the Service. We use third-party services for payment
        processing.
      </p>
      <p className={css.p}>
        We will not store or collect Your payment card details. That information is provided
        directly to Our third-party payment processor whose use of Your personal information is
        governed by their privacy policy. Our payment processor adheres to the standards set by
        PCI-DSS as managed by the PCI Security Standards Council.
      </p>
      <ul className={css.ul}>
        <li className={css.li}>
          <strong>Stripe</strong> — payment processor and Connect platform for Expert payouts.
          Privacy policy:{' '}
          <a
            href="https://stripe.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={css.link}
          >
            stripe.com/privacy
          </a>
          .
        </li>
      </ul>

      <h3 className={css.h3}>4.4 — Behavioral remarketing</h3>
      <p className={css.p}>
        The Company may use remarketing services to advertise to You after You have accessed or
        visited our Service. We and Our third-party vendors may use cookies and non-cookie
        technologies to help Us recognize Your Device and understand how You use our Service so
        that We can serve You advertisements that are likely to be of interest to You.
      </p>
      <p className={css.p}>You can opt out of personalized advertising via:</p>
      <ul className={css.ul}>
        <li className={css.li}>
          The NAI&apos;s opt-out platform:{' '}
          <a
            href="http://www.networkadvertising.org/choices/"
            target="_blank"
            rel="noopener noreferrer"
            className={css.link}
          >
            networkadvertising.org/choices
          </a>
        </li>
        <li className={css.li}>
          The EDAA&apos;s opt-out platform:{' '}
          <a
            href="http://www.youronlinechoices.com/"
            target="_blank"
            rel="noopener noreferrer"
            className={css.link}
          >
            youronlinechoices.com
          </a>
        </li>
        <li className={css.li}>
          The DAA&apos;s opt-out platform:{' '}
          <a
            href="http://optout.aboutads.info/?c=2&lang=EN"
            target="_blank"
            rel="noopener noreferrer"
            className={css.link}
          >
            optout.aboutads.info
          </a>
        </li>
        <li className={css.li}>
          Mobile device settings: &ldquo;Limit Ad Tracking&rdquo; (iOS) or &ldquo;Opt Out of Ads
          Personalization&rdquo; (Android).
        </li>
      </ul>
    </section>

    {/* 5 — Links */}
    <section id="links" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>5</span> Links to other websites
      </h2>
      <p className={css.p}>
        Our Service may contain links to other websites that are not operated by Us. If You click
        on a third-party link, You will be directed to that third party&apos;s site. We strongly
        advise You to review the privacy policy of every site You visit.
      </p>
      <p className={css.p}>
        We have no control over and assume no responsibility for the content, privacy policies, or
        practices of any third-party sites or services.
      </p>
    </section>

    {/* 6 — GDPR */}
    <section id="gdpr" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>6</span> GDPR privacy
      </h2>

      <h3 className={css.h3}>6.1 — Legal basis for processing personal data under GDPR</h3>
      <p className={css.p}>We may process Personal Data under the following conditions:</p>
      <ul className={css.ul}>
        <li className={css.li}>
          <strong>Consent</strong>: You have given Your consent for processing Personal Data for
          one or more specific purposes.
        </li>
        <li className={css.li}>
          <strong>Performance of a contract</strong>: provision of Personal Data is necessary for
          the performance of an agreement with You.
        </li>
        <li className={css.li}>
          <strong>Legal obligations</strong>: processing Personal Data is necessary for compliance
          with a legal obligation to which the Company is subject.
        </li>
        <li className={css.li}>
          <strong>Vital interests</strong>: processing Personal Data is necessary to protect Your
          vital interests or those of another natural person.
        </li>
        <li className={css.li}>
          <strong>Public interests</strong>: processing Personal Data is related to a task that is
          carried out in the public interest.
        </li>
        <li className={css.li}>
          <strong>Legitimate interests</strong>: processing Personal Data is necessary for the
          purposes of the legitimate interests pursued by the Company.
        </li>
      </ul>

      <h3 className={css.h3}>6.2 — Your rights under the GDPR</h3>
      <p className={css.p}>
        The Company undertakes to respect the confidentiality of Your Personal Data and to
        guarantee You can exercise Your rights. You have the right under this Privacy Policy, and
        by law if You are within the EU, to:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>
          <strong>Request access</strong> to Your Personal Data and receive a copy of what We hold.
        </li>
        <li className={css.li}>
          <strong>Request correction</strong> of any incomplete or inaccurate information.
        </li>
        <li className={css.li}>
          <strong>Object to processing</strong> based on legitimate interest, or for direct
          marketing purposes.
        </li>
        <li className={css.li}>
          <strong>Request erasure</strong> of Your Personal Data when there is no good reason for
          Us to continue processing it.
        </li>
        <li className={css.li}>
          <strong>Request transfer</strong> of Your Personal Data to You or a chosen third party in
          a structured, commonly used, machine-readable format.
        </li>
        <li className={css.li}>
          <strong>Withdraw consent</strong> on the use of Your Personal Data — though if You
          withdraw consent, We may not be able to provide certain functionality.
        </li>
      </ul>

      <h3 className={css.h3}>6.3 — Exercising your GDPR data protection rights</h3>
      <p className={css.p}>
        You may exercise Your rights of access, rectification, cancellation, and opposition by
        contacting Us. We may ask You to verify Your identity before responding. We will respond as
        soon as possible.
      </p>
      <p className={css.p}>
        You have the right to complain to a Data Protection Authority about Our collection and use
        of Your Personal Data. If You are in the European Economic Area (EEA), please contact Your
        local data protection authority.
      </p>
    </section>

    {/* 7 — CCPA */}
    <section id="ccpa" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>7</span> CCPA, CalOPPA, and California privacy rights
      </h2>
      <p className={css.p}>
        This section supplements the information contained in Our Privacy Policy and it applies
        solely to all visitors, users, and others who reside in the State of California.
      </p>

      <h3 className={css.h3}>7.1 — Categories of personal information collected</h3>
      <p className={css.p}>
        We collect information that identifies, relates to, describes, references, is capable of
        being associated with, or could reasonably be linked, directly or indirectly, with a
        particular Consumer or Device. The following categories may have been collected from
        California residents within the last twelve (12) months:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>
          <strong>Category A — Identifiers</strong> (real name, alias, postal address, unique
          identifier, IP address, email address, account name): <em>Yes</em>.
        </li>
        <li className={css.li}>
          <strong>Category B — California Customer Records categories</strong> (name, signature,
          address, telephone number, bank account number, credit card number): <em>Yes</em>.
        </li>
        <li className={css.li}>
          <strong>Category C — Protected classification characteristics</strong>: <em>No</em>.
        </li>
        <li className={css.li}>
          <strong>Category D — Commercial information</strong> (records of services purchased or
          considered): <em>Yes</em>.
        </li>
        <li className={css.li}>
          <strong>Category E — Biometric information</strong>: <em>No</em>.
        </li>
        <li className={css.li}>
          <strong>Category F — Internet or other similar network activity</strong>: <em>Yes</em>.
        </li>
        <li className={css.li}>
          <strong>Category G — Geolocation data</strong>: <em>No</em>.
        </li>
        <li className={css.li}>
          <strong>Category H — Sensory data</strong> (audio/video from Sessions, when recordings
          are made and stored with consent): <em>Yes, with consent</em>.
        </li>
        <li className={css.li}>
          <strong>Category I — Professional or employment-related information</strong> (Experts
          only — career background, business operating history): <em>Yes</em>.
        </li>
        <li className={css.li}>
          <strong>Category J — Non-public education information</strong>: <em>No</em>.
        </li>
        <li className={css.li}>
          <strong>Category K — Inferences</strong> drawn from other personal information:{' '}
          <em>No</em>.
        </li>
      </ul>

      <h3 className={css.h3}>7.2 — Sources of personal information</h3>
      <p className={css.p}>
        We obtain the categories of personal information listed above from the following
        categories of sources:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>
          <strong>Directly from You</strong> — for example, from forms You complete on our Service
          or preferences You express through our Service.
        </li>
        <li className={css.li}>
          <strong>Indirectly from You</strong> — for example, from observing Your activity on our
          Service.
        </li>
        <li className={css.li}>
          <strong>Automatically from You</strong> — for example, through cookies We or our Service
          Providers set on Your Device.
        </li>
        <li className={css.li}>
          <strong>From Service Providers</strong> — for example, third-party vendors for analytics
          or payment processing.
        </li>
      </ul>

      <h3 className={css.h3}>7.3 — Use of personal information for business purposes</h3>
      <p className={css.p}>
        We may use or disclose personal information We collect for business or commercial
        purposes, including: operating our Service, providing You support, fulfilling Your
        requests, responding to law enforcement requests, internal administrative and auditing
        purposes, and detecting security incidents.
      </p>

      <h3 className={css.h3}>7.4 — Sale of personal information</h3>
      <p className={css.p}>
        <strong>We do not sell Your personal information for monetary consideration.</strong> Some
        sharing of personal information with our Service Providers (for analytics, advertising,
        and similar purposes) may fall under the broad CCPA definition of &ldquo;sale&rdquo;. If
        You wish to opt out of such sharing, please contact Us or use the opt-out tools listed in
        section 4.4.
      </p>

      <h3 className={css.h3}>7.5 — Sharing of personal information</h3>
      <p className={css.p}>
        We may share Your personal information with: Service Providers, payment processors, our
        affiliates, and third-party vendors to whom You authorize Us to disclose Your personal
        information.
      </p>

      <h3 className={css.h3}>7.6 — Sale of personal information of minors under 16</h3>
      <p className={css.p}>
        We do not knowingly collect personal information from minors under the age of 16, and we
        do not sell the personal information of Consumers We actually know are less than 16 years
        of age.
      </p>

      <h3 className={css.h3}>7.7 — Your rights under the CCPA</h3>
      <p className={css.p}>
        The CCPA provides California residents with specific rights regarding their personal
        information. If You are a California resident, You have the following rights:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>
          <strong>The right to notice</strong> — to be notified which categories of Personal Data
          are being collected and the purposes.
        </li>
        <li className={css.li}>
          <strong>The right to request</strong> disclosure of information We collected, shared, or
          used in the previous 12 months.
        </li>
        <li className={css.li}>
          <strong>The right to opt out</strong> of the sale (or sharing under CCPA definitions) of
          personal information.
        </li>
        <li className={css.li}>
          <strong>The right to delete</strong> Your Personal Data, subject to certain exceptions
          (legal obligations, ongoing transactions, security incidents).
        </li>
        <li className={css.li}>
          <strong>The right not to be discriminated against</strong> for exercising any of Your
          consumer rights.
        </li>
      </ul>

      <h3 className={css.h3}>7.8 — Exercising your CCPA rights</h3>
      <p className={css.p}>
        To exercise any of Your rights under the CCPA, contact Us at{' '}
        <a href={`mailto:${LEGAL_INFO.contactEmail}`} className={css.link}>
          {LEGAL_INFO.contactEmail}
        </a>
        . Only You, or a person registered with the California Secretary of State that You
        authorize to act on Your behalf, may make a verifiable request related to Your personal
        information. We will disclose and deliver the required information free of charge within 45
        days of receiving Your verifiable request.
      </p>

      <h3 className={css.h3}>7.9 — Do Not Track signals (CalOPPA)</h3>
      <p className={css.p}>
        Our Service does not currently respond to Do Not Track signals. Some third-party sites do
        keep track of Your browsing activities; You can set Your preferences in Your web browser
        to inform websites that You do not want to be tracked.
      </p>
    </section>

    {/* 8 — Children */}
    <section id="children" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>8</span> Children&apos;s privacy rights
      </h2>

      <h3 className={css.h3}>8.1 — General privacy rights for children</h3>
      <p className={css.p}>
        Our Service does not address anyone under the age of 13. We do not knowingly collect
        personally identifiable information from anyone under the age of 13. If You are a parent or
        guardian and You are aware that Your child has provided Us with Personal Data, please
        contact Us. If We become aware that We have collected Personal Data from anyone under the
        age of 13 without verification of parental consent, We take steps to remove that
        information from Our servers.
      </p>

      <h3 className={css.h3}>8.2 — California privacy rights for minor users</h3>
      <p className={css.p}>
        California Business and Professions Code Section 22581 allows California residents under
        the age of 18 who are registered users of online services to request and obtain removal of
        content or information they have publicly posted. To request removal, contact Us using the
        contact information above and include the email address associated with Your account.
      </p>
    </section>

    {/* 9 — Changes */}
    <section id="changes" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>9</span> Changes to this Privacy Policy
      </h2>
      <p className={css.p}>
        We may update Our Privacy Policy from time to time. We will notify You of any changes by
        posting the new Privacy Policy on this page.
      </p>
      <p className={css.p}>
        We will let You know via email and/or a prominent notice on Our Service prior to the
        change becoming effective and update the &ldquo;Last updated&rdquo; date at the top of
        this Privacy Policy.
      </p>
      <p className={css.p}>
        You are advised to review this Privacy Policy periodically for any changes. Changes to
        this Privacy Policy are effective when they are posted on this page.
      </p>
    </section>
  </article>
);

/* ============================================================================
   PrivacyPolicyContent — content-only render for modal contexts (signup flow).
   ============================================================================ */
export const PrivacyPolicyContent = () => (
  <div className={css.modalContent}>
    <h2 className={css.modalHeading}>
      Privacy <em>policy</em>
    </h2>
    <p className={css.modalLastUpdated}>
      Last updated: <strong>{LEGAL_INFO.lastUpdated}</strong>
    </p>
    <PrivacyArticleBody />
  </div>
);

/* ============================================================================
   Full PrivacyPolicyPage
   ============================================================================ */
export const PrivacyPolicyPageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();

  const title = intl.formatMessage({
    id: 'PrivacyPolicyPage.title',
    defaultMessage: 'Privacy Policy — DoneThat',
  });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<Footer />}>
        <div className={css.root}>
          {/* ===== Hero ===== */}
          <header className={css.hero}>
            <div className={css.heroBg} aria-hidden />
            <div className={css.heroInner}>
              <span className={css.eyebrow}>Legal</span>
              <Heading as="h1" rootClassName={css.heroHeading}>
                Privacy <em>policy</em>.
              </Heading>
              <p className={css.heroDescription}>
                How {LEGAL_INFO.marketplaceName} collects, uses, and protects your information when
                you use the Service. This policy describes your rights — including under GDPR and
                CCPA — and how we honor them.
              </p>
              <p className={css.lastUpdated}>
                <span className={css.lastUpdatedDot} aria-hidden /> Last updated:{' '}
                <strong>{LEGAL_INFO.lastUpdated}</strong>
              </p>
            </div>
          </header>

          {/* ===== Body ===== */}
          <div className={css.body}>
            <div className={css.bodyInner}>
              <aside className={css.toc} aria-label="Table of contents">
                <span className={css.tocLabel}>Contents</span>
                <ol className={css.tocList}>
                  {TOC.map(item => (
                    <li key={item.id} className={css.tocItem}>
                      <a href={`#${item.id}`} className={css.tocLink}>
                        <span className={css.tocNumber}>{item.number}</span>
                        <span>{item.title}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </aside>

              <PrivacyArticleBody />
            </div>
          </div>

          {/* ===== Bottom help card ===== */}
          <section className={css.helpSection}>
            <div className={css.helpCard}>
              <div>
                <span className={css.helpEyebrow}>Privacy or data questions?</span>
                <h2 className={css.helpHeading}>
                  We answer every privacy email <em>personally</em>.
                </h2>
                <p className={css.helpText}>
                  Whether you want a data export, deletion, or just have a question — write to us
                  at{' '}
                  <a href={`mailto:${LEGAL_INFO.contactEmail}`} className={css.helpLink}>
                    {LEGAL_INFO.contactEmail}
                  </a>{' '}
                  and a real person will reply.
                </p>
              </div>
              <div className={css.helpActions}>
                <NamedLink name="FAQPage" className={css.helpPrimary}>
                  Visit FAQ
                </NamedLink>
                <a href={`mailto:${LEGAL_INFO.contactEmail}`} className={css.helpSecondary}>
                  Email us
                </a>
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const EnhancedPrivacyPolicyPage = props => {
  const intl = useIntl();
  return <PrivacyPolicyPageComponent intl={intl} {...props} />;
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const PrivacyPolicyPage = compose(connect(mapStateToProps))(EnhancedPrivacyPolicyPage);

export default PrivacyPolicyPage;
